import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { generateLabelBatch } from "@/lib/fulfillment.functions";
import { listBulkPackShipments, evaluateBulkPackEligibility } from "@/lib/pack-detail.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { StatusPill, statusTone } from "@/components/ui/status-pill";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Printer, Loader2, AlertTriangle, ArrowLeft, MapPin, PackageOpen, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/app/pack/bulk")({ component: BulkPack });

type Shipment = {
  id: string;
  order_id: string;
  carrier: string | null;
  service_level: string | null;
  status: string;
  label_url: string | null;
  tracking_number: string | null;
  packed_at: string | null;
};

function BulkPack() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { tenantId, facilityId } = useScope();
  const generateLabelBatchFn = useServerFn(generateLabelBatch);
  const listBulkShipmentsFn = useServerFn(listBulkPackShipments);
  const evalBulkFn = useServerFn(evaluateBulkPackEligibility);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  // Station banner — read-only display (set on /app/pack).
  const [station, setStation] = useState<string | null>(null);
  useEffect(() => {
    if (!facilityId) return;
    const k = `pack-station:${facilityId}`;
    setStation(typeof window !== "undefined" ? window.localStorage.getItem(k) : null);
  }, [facilityId]);

  const { data: shipments = [], isLoading } = useQuery({
    queryKey: ["bulk-pack-shipments", tenantId, facilityId],
    enabled: !!tenantId && !!facilityId,
    queryFn: () => listBulkShipmentsFn({ data: { tenantId: tenantId!, facilityId: facilityId! } }) as Promise<Shipment[]>,
  });

  const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);

  const { data: eligibility } = useQuery({
    queryKey: ["bulk-pack-eligibility", tenantId, ids.slice().sort().join(",")],
    enabled: !!tenantId && ids.length > 0,
    queryFn: () => evalBulkFn({ data: { tenantId: tenantId!, shipmentIds: ids } }),
  });
  const results = (eligibility?.results ?? {}) as Record<string, { eligible: boolean; reason: string | null }>;
  const ineligible = ids.filter((id) => results[id] && !results[id].eligible);
  const blocked = ineligible.length > 0;

  const toggleAll = () => {
    const allSelected = shipments.length > 0 && shipments.every((s) => selected[s.id]);
    if (allSelected) setSelected({});
    else {
      const next: Record<string, boolean> = {};
      shipments.forEach((s) => { next[s.id] = true; });
      setSelected(next);
    }
  };

  const printBatch = async () => {
    if (ids.length === 0) return toast.error("Select shipments");
    if (blocked) {
      return toast.error(`${ineligible.length} shipment(s) cannot be bulk packed — see reasons below`);
    }
    setBusy(true);
    try {
      await runServerFn(
        () => generateLabelBatchFn({ data: { shipmentIds: ids } }),
        "Print batch",
      );
      toast.success(`Queued ${ids.length} labels`);
      setSelected({});
      qc.invalidateQueries({ queryKey: ["bulk-pack-shipments"] });
    } catch {
      // toast handled
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            <Link to="/app/pack" className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <span className="flex items-center gap-2">
              <Printer className="h-5 w-5 text-muted-foreground" />
              Print queue
            </span>
            <Badge variant="outline" className="text-[11px]">{shipments.length} waiting</Badge>
          </span>
        }
        description={`Select packed shipments and queue their labels for printing${station ? ` at ${station}` : ""}.`}
        actions={
          <Button onClick={printBatch} disabled={busy || ids.length === 0 || blocked} size="touch">
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Printer className="h-4 w-4 mr-2" />}
            Print {ids.length || ""} labels
          </Button>
        }
      />
      <div className="p-4 sm:p-6 space-y-4">
        {station && (
          <div className="flex items-center gap-2 rounded-lg border bg-card px-3 py-2 text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <span className="text-muted-foreground">You're at</span>
            <span className="font-semibold">{station}</span>
            <span className="text-muted-foreground">— labels print to this station's printer.</span>
          </div>
        )}

        {blocked && (
          <div className="flex items-start gap-2 rounded-md border-2 border-amber-500/40 bg-amber-500/5 px-3 py-2 text-sm">
            <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600" />
            <div>
              <div className="font-medium">{ineligible.length} shipment(s) blocked from bulk print</div>
              <div className="text-xs text-muted-foreground">
                Serialized SKUs must be packed individually. Lot-tracked SKUs may only batch when every selected order shares the same lot.
              </div>
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between py-3">
            <CardTitle className="text-sm">Packed shipments awaiting labels</CardTitle>
            <Button variant="ghost" size="sm" onClick={toggleAll} disabled={shipments.length === 0}>
              {shipments.length > 0 && shipments.every((s) => selected[s.id])
                ? <><CheckSquare className="h-3.5 w-3.5 mr-1" />Clear all</>
                : <><Square className="h-3.5 w-3.5 mr-1" />Select all</>}
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="text-xs">Shipment</TableHead>
                  <TableHead className="text-xs">Carrier</TableHead>
                  <TableHead className="text-xs">Tracking</TableHead>
                  <TableHead className="text-xs">Status</TableHead>
                  <TableHead className="text-xs">Eligibility</TableHead>
                  <TableHead className="w-24 text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground text-sm">Loading…</TableCell></TableRow>
                ) : shipments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                      <PackageOpen className="h-6 w-6 mx-auto mb-2 opacity-50" />
                      <div className="text-sm">No packed shipments waiting for labels.</div>
                    </TableCell>
                  </TableRow>
                ) : shipments.map((s) => {
                  const blockedRow = results[s.id] && !results[s.id].eligible;
                  return (
                    <TableRow
                      key={s.id}
                      className={`cursor-pointer hover:bg-muted/40 ${blockedRow ? "bg-amber-500/5" : ""}`}
                      onClick={() => navigate({ to: "/app/shipments/$shipmentId", params: { shipmentId: s.id } })}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={!!selected[s.id]}
                          onCheckedChange={(v) => setSelected((x) => ({ ...x, [s.id]: !!v }))}
                          aria-label={`Select shipment ${s.id.slice(0, 8)}`}
                        />
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.id.slice(0, 8)}</TableCell>
                      <TableCell className="text-xs">
                        {s.carrier ?? "—"}{s.service_level ? ` · ${s.service_level}` : ""}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{s.tracking_number ?? "—"}</TableCell>
                      <TableCell><StatusPill tone={statusTone(s.status)} dot>{s.status}</StatusPill></TableCell>
                      <TableCell className="text-xs">
                        {!selected[s.id] ? <span className="text-muted-foreground">—</span>
                          : results[s.id] == null ? <span className="text-muted-foreground">Checking…</span>
                          : results[s.id].eligible ? <span className="text-success">OK</span>
                          : <span className="text-warning">{results[s.id].reason}</span>}
                      </TableCell>
                      <TableCell className="text-right text-xs text-muted-foreground">Open →</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  );
}